import React from "react";
import { useSelector } from "react-redux";

import classes from "./CreateScalePage.module.css";

import ChartEditor from "./ChartEditor/ChartEditor";
import ChartDataToSVG from "../code/ChartDataToSVG";
import ImageGridItem from "./ImageGridItem/ImageGridItem";
import BottomMenu from "../BottomMenu/BottomMenu";
import Card from "../UI/Card";
import RequestSupportCard from "../UI/RequestSupportCard";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ChartWorkflow, WorkflowActions } from "../ChartWorkflow";
import CreateQuickTour from "./CreateQuickTour";

function CreateScalePage() {
  // this is the best fit set of charts data
  const createdData = useSelector((state) => state.createdData);

  let maxImageWidth = 0;

  const imageList = createdData.map((pos, index) => {
    const svgData = ChartDataToSVG(pos.chartData, pos.maxFret);
    const image = svgData.image;
    if (svgData.width > maxImageWidth) {
      maxImageWidth = svgData.width;
    }
    // {JSON.stringify(pos, null, 2)}
    return (
      <ImageGridItem
        key={index}
        height={svgData.height}
        width={svgData.width}
        src={`data:image/svg+xml;utf8,${encodeURIComponent(image)}`}
        alt="Guitar Chart"
        tourTarget={index === 0 ? "create-diagrams" : undefined}
      />
    );
  });
  // for the left and rigth buttons
  maxImageWidth += 0;

  return (
    <div className={classes.background}>
      <HelmetProvider>
        <Helmet>
          <meta charset="UTF-8" />
          <title>
            Guitar Charts Designer
          </title>
          <meta
            name="description"
            content="Guitar Charts Designer is a web app that lets you create and print custom guitar charts and diagrams."
          />
        </Helmet>
      </HelmetProvider>
      <Card>
        <div className={classes.centred}>
          <h1 className={classes.h1}>Guitar Charts Designer</h1>
          <div className={classes.introduction}>
            <p>Create custom scale and arpeggio diagrams for 6, 7 and 8-string guitars, bass and ukulele.</p>
            <p>Automatically generate fretboard positions, select and customise your favourite fingerings,
              and download complete PDF chart sets or individual graphics for your own documents,
              videos and practice materials.</p>
          </div>
          <p className={classes.howtouselink}>
            <a href="#usage">How To Use...</a>
            <CreateQuickTour />
          </p>
        </div>
      </Card>
      <ChartWorkflow step={1} />
      <Card>
        <ChartEditor />
      </Card>
      <Card>
        <div
          className={classes.imageGrid}
          style={{
            gridTemplateColumns: `repeat(auto-fit, minmax(${maxImageWidth}px, 1fr))`,
            justifyItems: "center",
            alignItems: "center",
          }}
        >
          {imageList}
        </div>
      </Card>
      <WorkflowActions step={1} />
      <Card>
        <section className={classes.prose} aria-labelledby="capabilities-heading">
          <h2 id="capabilities-heading">What can you create with Guitar Charts?</h2>

          <h3>Generate scale and arpeggio diagrams</h3>
          <p>Create fretboard diagrams for major and minor scales, pentatonics, modes,
            arpeggios and custom combinations of intervals.</p>
          <p>Guitar Charts automatically generates different positions of your selected
            scale or arpeggio across the fretboard.</p>
          <p>Choose from 6, 7 and 8-string guitars, 4 and 5-string basses, or ukulele.
            Standard and alternative tunings are supported, and you can define your
            own custom instrument layout.</p>

          <h3>Select and customise your favourite fingerings</h3>
          <p>Not every automatically generated fingering will suit your playing style
            or musical requirements.</p>
          <p>The Refine editor lets you review the generated positions,{" "}
            <strong>discard the fingerings you don't want and keep the ones you find most useful</strong>.</p>
          <p>You can then customise individual diagrams by moving eligible notes
            between adjacent strings while preserving their pitch, removing
            unnecessary notes, or duplicating diagrams to create alternative fingerings.</p>
          <p>This allows you to build a collection of scale or arpeggio positions
            tailored to your own playing or teaching requirements.</p>

          <h3>Create complete printable PDF documents</h3>
          <p>Combine your selected diagrams into a complete chart set, with a title
            and optional explanatory text.</p>
          <p>Adjust the overall diagram size, preview the finished layout and download
            the complete document as a PDF.</p>
          <p>Ideal for lesson handouts, personal practice material and guitar teaching resources.</p>

          <h3>Download individual guitar diagrams</h3>
          <p>You can also download your individual fretboard diagrams as SVG and PNG graphics.
            The Download images button packages these together in a ZIP file.</p>
          <p>Use them in your own documents, instructional videos, presentations or teaching materials.</p>
          <p>SVG files provide scalable vector artwork, allowing diagrams to be
            resized without losing quality. PNG files are suitable for applications
            that use raster images.</p>
          <p>Both formats preserve your customised fingerings, note selections and colours.</p>

          <h3>Share your charts</h3>
          <p>Publish your chart set to create a shareable link that anyone can open without registering.</p>
          <p>You can also download charts without signing in.</p>
          <p>Registered users can save complete, editable chart sets privately and return to them later.</p>
          <p>Publishing a shareable link does not add your work to the public Library.</p>
        </section>
      </Card>
      <Card>
        <section className={classes.prose} aria-labelledby="usage">
          <h2 id="usage">How to use the Chart Designer</h2>

          <h3>Create your diagrams</h3>
          <p>Choose your instrument and tuning using <strong>Guitar Setup</strong>.</p>
          <p>Select a predefined scale or arpeggio using <strong>Scale</strong>, or customise
            the notes by selecting individual intervals in the <strong>Scale Note</strong> checkboxes.</p>
          <p>Click the associated <strong>Color</strong> boxes to highlight particular
            intervals using the colour picker.</p>
          <p>Select a <strong>Layout</strong> to control how the notes are arranged across the strings.</p>
          <p>Guitar Charts automatically generates the available positions for your chosen configuration.</p>

          <h3>Refine your charts</h3>
          <p>Select <strong>Refine</strong> to review the generated diagrams.</p>
          <p>Remove unwanted positions, customise individual fingerings, and duplicate
            diagrams where you want to create alternative versions.</p>
          <p>Add a document title and optional explanatory text.</p>
          <p>When you're satisfied, select <strong>Preview</strong> to inspect your finished
            document and download the results.</p>
          <p>Select <strong>Publish</strong> to open the clean, printable and shareable version.</p>

          <h3>Custom instrument layouts</h3>
          <p>For custom tunings, select <strong>Custom Layout</strong> in Guitar Setup.</p>
          <p>Choose the pitch of your lowest string and enter the semitone intervals
            between successive strings as a comma-separated list.</p>
          <p>For example, <code>5,5,5,4,5</code> represents the intervals between the
            strings of a standard six-string guitar.</p>
          <p>Each number is the fret on the lower string that matches the next open
            string: the fifth fret for E to A, A to D and D to G, the fourth fret
            for G to B, then the fifth fret for B to E.</p>
        </section>
      </Card>
      <RequestSupportCard />
      <Card>
        <BottomMenu />
      </Card>
    </div>
  );
}

export default CreateScalePage;
